import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../context/auth/AuthContext';
import { loadStripe } from '@stripe/stripe-js';

const SetupBilling = () => {
  const { userEmail, userId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Payment state
  const [fixedAmount, setFixedAmount] = useState('399.00');
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'link'
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // Credit card payment data
  const [cardPaymentData, setCardPaymentData] = useState({
    customerEmail: '',
    customerName: ''
  });
  
  // Payment link data
  const [paymentLinkData, setPaymentLinkData] = useState({
    customerEmail: '',
    customerName: '',
    paymentLink: '',
    linkGenerated: false
  });
  
  // Check if user is admin
  const isAdmin = () => {
    const chatbotUser = localStorage.getItem('chatbotUser');
    let adminEmail = '';
    
    if (chatbotUser) {
      try {
        const user = JSON.parse(chatbotUser);
        adminEmail = user.email || '';
      } catch (e) {
        console.error('Error parsing chatbotUser:', e);
      }
    }
    
    if (!adminEmail) {
      adminEmail = localStorage.getItem('userEmail') || '';
    }
    
    if (!adminEmail && userEmail) {
      adminEmail = userEmail;
    }
    
    return adminEmail.toLowerCase() === 'info@urcommunity.com.au';
  };

  useEffect(() => {
    if (!isAdmin()) {
      setError('Access denied. Admin privileges required.');
    }
  }, []);
  
  // Handle card payment input changes
  const handleCardPaymentInputChange = (e) => {
    const { name, value } = e.target;
    setCardPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle payment link input changes
  const handlePaymentLinkInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentLinkData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Redirect to Stripe Checkout for credit card payment
  const redirectToStripeCheckout = async () => {
    setIsProcessingPayment(true);
    setError(null);
    setSuccess(null);
    
    try {
      const amount = parseFloat(fixedAmount);
      
      if (!amount || amount <= 0) {
        throw new Error('Please enter a valid payment amount');
      }
      
      const customerEmail = cardPaymentData.customerEmail || '';
      const customerName = cardPaymentData.customerName || '';
      const monthYear = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
      
      console.log('🔄 Starting Stripe checkout process for Setup Billing...');
      console.log('📋 Checkout session data:', {
        amount: amount,
        monthYear: monthYear,
        customerEmail: customerEmail,
        customerName: customerName
      });
      
      // Create checkout session
      const response = await fetch(`${API_BASE_URL}/api/create-setup-billing-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amount,
          customerEmail: customerEmail,
          customerName: customerName,
          description: `Payment of $${amount.toFixed(2)}`
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Backend error response:', errorText);
        throw new Error(`Backend error: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('📄 Backend response data:', data);
      
      if (!data.sessionId) {
        console.error('❌ No sessionId in response:', data);
        throw new Error('No session ID received from server');
      }
      
      console.log('✅ Session ID received:', data.sessionId);
      
      // Load Stripe and redirect to checkout
      const stripe = await loadStripe('pk_live_51RMOUfJZKrN6X9pNIE0K4ADsWn0K1t2zovlkrnmKQIcA0bHhNs141ofA1UlX2ctp0OWZvv5tSEVZG7kLKVKKYCuu00ZMM27ciP');
      
      if (!stripe) {
        console.error('❌ Failed to load Stripe');
        throw new Error('Failed to load Stripe payment system');
      }
      
      console.log('🔄 Redirecting to Stripe checkout...');
      const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
      
      if (error) {
        console.error('❌ Stripe redirect error:', error);
        throw new Error(`Error opening payment page: ${error.message}`);
      } else {
        console.log('✅ Successfully redirected to Stripe checkout');
      }
    } catch (error) {
      console.error('❌ Error in redirectToStripeCheckout:', error);
      setError(error.message || 'Error creating payment session. Please try again.');
      setIsProcessingPayment(false);
    }
  };
  
  // Generate and send payment link
  const handleGeneratePaymentLink = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const amountInCents = Math.round(parseFloat(fixedAmount) * 100);
      
      // Create payment link via API
      const response = await fetch(`${API_BASE_URL}/api/create-payment-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: amountInCents,
          currency: 'aud',
          customerEmail: paymentLinkData.customerEmail,
          customerName: paymentLinkData.customerName,
          description: `Payment for ${paymentLinkData.customerName || paymentLinkData.customerEmail}`
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create payment link');
      }
      
      const data = await response.json();
      
      if (data.success && data.paymentLink) {
        setPaymentLinkData(prev => ({
          ...prev,
          paymentLink: data.paymentLink,
          linkGenerated: true
        }));
        
        // Show success message based on email sending status
        if (data.emailSent) {
          setSuccess(`Payment link generated and sent successfully to ${paymentLinkData.customerEmail}!`);
        } else if (data.emailError) {
          setSuccess(`Payment link generated successfully! However, email could not be sent: ${data.emailError}. The payment link is available below.`);
        } else {
          setSuccess('Payment link generated successfully!');
        }
      } else {
        throw new Error('Failed to generate payment link');
      }
    } catch (error) {
      console.error('Error generating payment link:', error);
      setError(error.message || 'Failed to generate payment link. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Send payment link via email
  const handleSendPaymentLink = async () => {
    if (!paymentLinkData.paymentLink || !paymentLinkData.customerEmail) {
      setError('Please generate a payment link first and provide customer email');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/send-payment-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: paymentLinkData.customerEmail,
          paymentLink: paymentLinkData.paymentLink,
          amount: fixedAmount,
          customerName: paymentLinkData.customerName
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send payment link');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Payment link sent successfully to ' + paymentLinkData.customerEmail);
      } else {
        throw new Error('Failed to send payment link');
      }
    } catch (error) {
      console.error('Error sending payment link:', error);
      setError(error.message || 'Failed to send payment link. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Copy payment link to clipboard
  const handleCopyPaymentLink = () => {
    if (paymentLinkData.paymentLink) {
      navigator.clipboard.writeText(paymentLinkData.paymentLink);
      setSuccess('Payment link copied to clipboard!');
    }
  };

  if (!isAdmin()) {
    const chatbotUser = localStorage.getItem('chatbotUser');
    let currentEmail = 'No email found';
    
    if (chatbotUser) {
      try {
        const user = JSON.parse(chatbotUser);
        currentEmail = user.email || 'No email found';
      } catch (e) {
        console.error('Error parsing chatbotUser:', e);
      }
    }
    
    if (currentEmail === 'No email found') {
      currentEmail = localStorage.getItem('userEmail') || 'No email found';
    }
    
    return (
      <div
        style={{
          background: '#fff',
          borderRadius: '50px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
          padding: '20px',
          minWidth: '900px',
          maxWidth: '1100px',
          width: '100%',
          color: '#111',
          marginTop: '0',
          marginBottom: '48px',
          height: '700px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <h2 style={{ fontWeight: 'bold', fontSize: '1.3rem', color: '#222', margin: 0, letterSpacing: '0.5px' }}>Setup Billing</h2>
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '16px',
          color: '#dc2626',
          marginTop: '20px'
        }}>
          <h3>Access denied. Admin privileges required.</h3>
          <p>Current user email: <strong>{currentEmail}</strong></p>
          <p>Required email: <strong>info@urcommunity.com.au</strong></p>
          <p>Please login with the correct admin email to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: '50px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        padding: '20px',
        minWidth: '900px',
        maxWidth: '1100px',
        width: '100%',
        color: '#111',
        marginTop: '0',
        marginBottom: '48px',
        maxHeight: '95vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
        <i className="fas fa-cog" style={{ fontSize: '24px', color: '#7c3aed', marginRight: '12px' }}></i>
        <h2 style={{ fontWeight: 'bold', fontSize: '1.3rem', color: '#222', margin: 0, letterSpacing: '0.5px' }}>
          Setup Billing
        </h2>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '16px',
          color: '#dc2626',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div style={{
          background: '#d1fae5',
          border: '1px solid #a7f3d0',
          borderRadius: '8px',
          padding: '16px',
          color: '#065f46',
          marginBottom: '20px'
        }}>
          {success}
        </div>
      )}

      {/* Content Area */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 0' }}>
        {/* Fixed Amount Section */}
        <div style={{
          background: '#f9fafb',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '20px'
        }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
            Fixed Payment Amount
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ fontSize: '0.95rem', fontWeight: '500', color: '#374151' }}>
              Amount (AU$):
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={fixedAmount}
              onChange={(e) => setFixedAmount(e.target.value)}
              style={{
                padding: '10px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '1rem',
                width: '150px',
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
            <span style={{ fontSize: '0.95rem', color: '#6b7280' }}>
              This amount will be used for all payments
            </span>
          </div>
        </div>

        {/* Payment Method Selection */}
        <div style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '20px'
        }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
            Payment Method
          </h3>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
            <button
              onClick={() => setPaymentMethod('card')}
              style={{
                flex: 1,
                padding: '16px',
                border: `2px solid ${paymentMethod === 'card' ? '#7c3aed' : '#e5e7eb'}`,
                borderRadius: '8px',
                background: paymentMethod === 'card' ? '#ede9fe' : '#fff',
                color: paymentMethod === 'card' ? '#7c3aed' : '#374151',
                fontWeight: paymentMethod === 'card' ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <i className="fas fa-credit-card"></i>
              Credit Card Payment
            </button>
            <button
              onClick={() => setPaymentMethod('link')}
              style={{
                flex: 1,
                padding: '16px',
                border: `2px solid ${paymentMethod === 'link' ? '#7c3aed' : '#e5e7eb'}`,
                borderRadius: '8px',
                background: paymentMethod === 'link' ? '#ede9fe' : '#fff',
                color: paymentMethod === 'link' ? '#7c3aed' : '#374151',
                fontWeight: paymentMethod === 'link' ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <i className="fas fa-link"></i>
              Send Payment Link
            </button>
          </div>

          {/* Credit Card Payment Form */}
          {paymentMethod === 'card' && (
            <div style={{ marginTop: '24px' }}>
              <div style={{ 
                background: '#f0f9ff', 
                border: '1px solid #bae6fd', 
                borderRadius: '8px', 
                padding: '16px', 
                marginBottom: '20px' 
              }}>
                <p style={{ margin: 0, fontSize: '0.95rem', color: '#1e40af' }}>
                  <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
                  You will be redirected to Stripe's secure checkout page to complete the payment using credit card, debit card, or other payment methods.
                </p>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.95rem', fontWeight: '500', color: '#374151' }}>
                    Customer Name
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={cardPaymentData.customerName}
                    onChange={handleCardPaymentInputChange}
                    placeholder="John Doe"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.95rem', fontWeight: '500', color: '#374151' }}>
                    Customer Email
                  </label>
                  <input
                    type="email"
                    name="customerEmail"
                    value={cardPaymentData.customerEmail}
                    onChange={handleCardPaymentInputChange}
                    placeholder="customer@example.com"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>
              
              <button
                onClick={redirectToStripeCheckout}
                disabled={isProcessingPayment}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  background: isProcessingPayment ? '#9ca3af' : '#7c3aed',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (!isProcessingPayment) e.target.style.background = '#6d28d9';
                }}
                onMouseLeave={(e) => {
                  if (!isProcessingPayment) e.target.style.background = '#7c3aed';
                }}
              >
                {isProcessingPayment ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Redirecting to Payment...
                  </>
                ) : (
                  <>
                    <i className="fas fa-credit-card"></i>
                    Proceed to Payment - AU$${fixedAmount}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Payment Link Form */}
          {paymentMethod === 'link' && (
            <div style={{ marginTop: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.95rem', fontWeight: '500', color: '#374151' }}>
                    Customer Name
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={paymentLinkData.customerName}
                    onChange={handlePaymentLinkInputChange}
                    placeholder="John Doe"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.95rem', fontWeight: '500', color: '#374151' }}>
                    Customer Email <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="customerEmail"
                    value={paymentLinkData.customerEmail}
                    onChange={handlePaymentLinkInputChange}
                    placeholder="customer@example.com"
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <button
                  onClick={handleGeneratePaymentLink}
                  disabled={loading || !paymentLinkData.customerEmail}
                  style={{
                    flex: 1,
                    padding: '14px 24px',
                    background: (loading || !paymentLinkData.customerEmail) ? '#9ca3af' : '#10b981',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: (loading || !paymentLinkData.customerEmail) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && paymentLinkData.customerEmail) e.target.style.background = '#059669';
                  }}
                  onMouseLeave={(e) => {
                    if (!loading && paymentLinkData.customerEmail) e.target.style.background = '#10b981';
                  }}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Generating & Sending Email...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-link"></i>
                      Generate Payment Link & Send Email
                    </>
                  )}
                </button>
              </div>
              
              {/* Display generated payment link */}
              {paymentLinkData.linkGenerated && paymentLinkData.paymentLink && (
                <div style={{
                  background: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.95rem', fontWeight: '500', color: '#374151' }}>
                    Payment Link:
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={paymentLinkData.paymentLink}
                      readOnly
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        border: '2px solid #bae6fd',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        background: '#fff',
                        color: '#1e40af'
                      }}
                    />
                    <button
                      onClick={handleCopyPaymentLink}
                      style={{
                        padding: '10px 16px',
                        background: '#3b82f6',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      title="Copy to clipboard"
                    >
                      <i className="fas fa-copy"></i>
                      Copy
                    </button>
                  </div>
                  <p style={{ marginTop: '8px', fontSize: '0.85rem', color: '#6b7280' }}>
                    Payment link has been sent to {paymentLinkData.customerEmail}. You can also share this link manually if needed.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupBilling;

